"use client";

import dayjs from 'dayjs';
import Image from 'next/image';
import React, { useState, useEffect } from 'react';
import { FiCheck, FiChevronRight, FiActivity, FiShield, FiPackage, FiCalendar } from "react-icons/fi";
import { IoIosGitCompare } from "react-icons/io";
import { LuPencil } from "react-icons/lu";
import { RiDoubleQuotesL, RiDoubleQuotesR } from "react-icons/ri";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/Components/ui/dialog';
import CrossIcon from "@/Components/Common/Icons/CrossIcon";
import RatingStar from "@/Components/Common/Icons/RatingStar";
import ReportReviewDialog from "./ReportReviewDialog";

// Import extracted components
import { useIntersectionObserver, RatingCircle, ConfidenceLevelIndicator, ConfidenceScoreDisplay, AccessibilityLevelIndicator, AccessibilityScoreDisplay, AnimatedProgressBar, AnimatedCounter, ReviewHeader, formatSpecialty, getPeerComparisonStatus, getStaticData, peerAverages, getRatingCategories } from './components';
import { CLINICAL_CONCERN } from './components/utils';
const ReviewCard = ({
  review,
  productName,
  productImage
}) => {
  const [showMore, setShowMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    if (!isModalOpen) {
      setShowMore(false);
    }
  }, [isModalOpen]);
  const staticData = getStaticData(review);
  const ratingCategories = getRatingCategories(review);

  // Full Review Content Component
  const FullReviewContent = ({
    hidePerformanceRatings = false
  }) => <div className="bg-white">
			{/* Prescriber Information Section */}
			<div className="px-8 py-12 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
				<div className="flex items-start gap-5 mb-8">
					<div className="relative">
						<div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg" style={{
            background: 'linear-gradient(135deg, #1E5EFF 0%, #3B82F6 100%)'
          }}>
							<span className="text-white text-2xl font-bold font-poppins">
								{formatSpecialty(review.physicianDetails?.speciality)?.charAt(0).toUpperCase() || "H"}
							</span>
						</div>
					</div>
					<div className="flex-1">
						<div className="flex items-center gap-3 mb-2">
							<h3 className="text-xl font-bold text-gray-900 font-poppins">
								{formatSpecialty(review.physicianDetails?.speciality)}
							</h3>
							<span className="inline-flex items-center w-5 h-5 justify-center text-[10px] font-bold font-poppins bg-green-100 text-green-700 rounded-full border border-green-200 uppercase tracking-wide">
								<FiCheck className="w-3 h-3 text-green-700" />
							</span>
						</div>
						<div className="flex items-center gap-2 mb-1.5">
							<div className="text-sm text-gray-700 font-poppins">
								{review.physicianDetails?.region || "Northeast US"}
							</div>
							<span className="text-sm text-gray-600 font-poppins">{" | "}</span>
							<div className="text-sm text-gray-600 font-poppins">
								{review.physicianDetails?.experience || "10+ years experience"}
							</div>
						</div>
						<div className="flex items-center gap-2 text-xs text-gray-500 font-poppins">
							<FiCalendar className="w-3.5 h-3.5" />
							<span>Reviewed on {dayjs(review.createdAt).format("MMMM DD, YYYY")}</span>
						</div>
					</div>
					<div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200">
						<div className="text-base font-bold text-primary-primaryBlue font-poppins">
							{review.individualAverage?.toFixed(1) || "0.0"}
						</div>
						<RatingStar className="w-3 h-3" fill="#1E5EFF" clipId={`review_detail_star_${review._id || 'default'}`} />
					</div>
				</div>

				{/* Review Text Bubble */}
				<div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-6
					before:content-[''] before:absolute before:block before:w-0 before:z-[1] before:border-solid before:border-b-white before:border-x-transparent before:border-t-0 before:border-b-[20px] before:border-x-[20px] before:top-[-20px] before:left-[6%] before:-ml-5
					after:content-[''] after:absolute after:block after:w-0 after:z-0 after:border-solid after:border-b-gray-200 after:border-x-transparent after:border-t-0 after:border-b-[21px] after:border-x-[21px] after:top-[-21.5px] after:left-[6%] after:-ml-[21px]">
					<div className="relative">
						<RiDoubleQuotesL className="absolute -left-2 -top-3 text-3xl text-primary-primaryBlue/20" />
						<div className="px-7">
							<div className="text-lg leading-loose text-black font-poppins whitespace-pre-wrap">
								{!showMore ? `${review.review.slice(0, 200)}${review.review.length > 200 ? "..." : ""}` : review.review}
							</div>
						</div>
						<RiDoubleQuotesR className="absolute -right-2 -bottom-3 text-3xl text-primary-primaryBlue/20" />
					</div>
					{review.review.length > 200 && <div className="mt-6 pt-4 border-t border-gray-100">
							<button onClick={() => setShowMore(!showMore)} className='inline-flex items-center gap-2 text-sm text-primary-primaryBlue hover:text-primary-primaryBlue/80 font-semibold transition-all hover:gap-3'>
								{showMore ? <>
										<span>Show less</span>
										<FiChevronRight className="w-4 h-4 transform rotate-90" />
									</> : <>
										<span>Read full review</span>
										<FiChevronRight className="w-4 h-4 transform -rotate-90" />
									</>}
							</button>
						</div>}
				</div>
			</div>

			{/* Performance Assessment */}
			{!hidePerformanceRatings && <PerformanceSection ratingCategories={ratingCategories} />}

			{/* Accessibility Assessment */}
			<AccessibilitySection staticData={staticData} />

			{/* Clinical Confidence Assessment */}
			<ConfidenceSection staticData={staticData} />

			{/* Competitor Comparison */}
			<CompetitorComparisonSection ratingCategories={ratingCategories} staticData={staticData} productName={productName} productImage={productImage} />

			{/* Clinical Insight */}
			<ClinicalInsightSection staticData={staticData} />
		</div>;

  // Performance Section Component
  const PerformanceSection = ({
    ratingCategories: ratingCategories_0
  }) => {
    const performanceObserver = useIntersectionObserver();
    return <div ref={performanceObserver.elementRef} className="px-8 py-16 border-b border-gray-200">
				<div className="flex items-center gap-3 mb-5">
					<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
						<FiActivity className="w-4 h-4 text-primary-primaryBlue" />
					</div>
					<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">CLINICAL PERFORMANCE</div>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
					{ratingCategories_0.map(category => {
          const rating = category.value;
          const roundedRating = Math.round(rating);
          let bgColor;
          if (roundedRating === 5) {
            bgColor = "#059669";
          } else if (roundedRating >= 3 && roundedRating <= 4) {
            bgColor = "#1E5EFF";
          } else if (roundedRating >= 1 && roundedRating <= 2) {
            bgColor = "#F59E0B";
          } else {
            bgColor = "#8B5CF6";
          }
          const peerAvg = peerAverages[category.tag];
          const peerStatus = getPeerComparisonStatus(rating, peerAvg);
          return <div key={category.tag} className="flex items-center gap-4 p-3 border border-gray-200 rounded bg-white">
								<RatingCircle rating={rating} roundedRating={roundedRating} bgColor={bgColor} label="" shouldAnimate={performanceObserver.hasBeenVisible} />
								<div className="flex-1 p-3">
									<div className="text-xs font-semibold text-gray-900 font-poppins mb-1">{category.label}</div>
									<div className="text-xs text-gray-500 mb-1">Your rating: {rating.toFixed(1)}/5.0</div>
									<div className='flex gap-2 items-center'>
										<div className="text-xs text-gray-400">Peer avg: {peerAvg.toFixed(1)}/5.0</div>
										<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${peerStatus.bgColor} ${peerStatus.color} border ${peerStatus.borderColor}`}>
											{peerStatus.label}
										</span>
									</div>
								</div>
							</div>;
        })}
				</div>

				{/* Clinical Concerns */}
				{(() => {
        const lowRatings = ratingCategories_0.filter(category_0 => {
          const rating_0 = category_0.value || 0;
          return rating_0 > CLINICAL_CONCERN.MIN && rating_0 <= CLINICAL_CONCERN.MIN;
        });
        if (lowRatings.length === 0) return null;
        return <div className="pt-3">
							<div className="text-sm uppercase tracking-wider text-yellow-700 font-bold mb-4 flex items-center gap-2">
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
								</svg>
								CLINICAL CONCERNS
							</div>
							<div className="flex flex-wrap gap-3 mb-4">
								{lowRatings.map(category_1 => <div key={category_1.tag} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-lg">
										<span className="text-sm font-semibold text-yellow-800 font-poppins">
											{category_1.label}
										</span>
										<span className="text-xs text-yellow-700 font-poppins">(Low)</span>
									</div>)}
							</div>
							<p className="text-sm text-gray-700 font-poppins leading-relaxed">
								This prescriber noted challenges with cost and access that may impact patient outcomes.
							</p>
						</div>;
      })()}
			</div>;
  };

  // Accessibility Section Component
  const AccessibilitySection = ({
    staticData: staticData_0
  }) => {
    const accessibilityObserver = useIntersectionObserver();
    return <div ref={accessibilityObserver.elementRef} className="px-8 py-16 border-b border-gray-200">
				<div className="flex items-center gap-3 mb-5">
					<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
						<FiPackage className="w-4 h-4 text-primary-primaryBlue" />
					</div>
					<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">ACCESSIBILITY</div>
				</div>
				<div className="flex items-center gap-8">
					<div className="flex-1 flex justify-center">
						<div className="flex flex-col items-center">
							<AccessibilityScoreDisplay accessibilityScore={staticData_0.accessibilityScore} accessibilityCategory={staticData_0.accessibilityCategory} shouldAnimate={accessibilityObserver.hasBeenVisible} />
							<div className="mt-3 text-center">
								<div className="text-xs text-gray-400 mb-1">Peer avg: {peerAverages.accessibility}%</div>
								{(() => {
                const diff = staticData_0.accessibilityScore - peerAverages.accessibility;
                const status = diff > 5 ? {
                  label: "Higher than peer average",
                  bgColor: "bg-green-50",
                  color: "text-green-600",
                  borderColor: "border-green-200"
                } : diff < -5 ? {
                  label: "Lower than peer average",
                  bgColor: "bg-orange-50",
                  color: "text-orange-600",
                  borderColor: "border-orange-200"
                } : {
                  label: "Aligned with peers",
                  bgColor: "bg-blue-50",
                  color: "text-blue-600",
                  borderColor: "border-blue-200"
                };
                return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${status.bgColor} ${status.color} border ${status.borderColor}`}>
											{status.label}
										</span>;
              })()}
							</div>
						</div>
					</div>
					<div className="flex-1 flex justify-center font-normal">
						<AccessibilityLevelIndicator currentScore={staticData_0.accessibilityScore} />
					</div>
				</div>
			</div>;
  };

  // Confidence Section Component
  const ConfidenceSection = ({
    staticData: staticData_1
  }) => {
    const confidenceObserver = useIntersectionObserver();
    return <div ref={confidenceObserver.elementRef} className="px-8 py-16 border-b border-gray-200">
				<div className="flex items-center gap-3 mb-5">
					<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
						<FiShield className="w-4 h-4 text-primary-primaryBlue" />
					</div>
					<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">CLINICAL CONFIDENCE</div>
				</div>
				<div className="flex items-center gap-8">
					<div className="flex-1 flex justify-center">
						<div className="flex flex-col items-center">
							<ConfidenceScoreDisplay confidenceScore={staticData_1.confidenceScore} confidenceCategory={staticData_1.confidenceCategory} shouldAnimate={confidenceObserver.hasBeenVisible} />
							<div className="mt-3 text-center">
								<div className="text-xs text-gray-400 mb-1">Peer avg: {peerAverages.confidence}%</div>
								{(() => {
                const diff_0 = staticData_1.confidenceScore - peerAverages.confidence;
                const status_0 = diff_0 > 5 ? {
                  label: "Higher than peer average",
                  bgColor: "bg-green-50",
                  color: "text-green-600",
                  borderColor: "border-green-200"
                } : diff_0 < -5 ? {
                  label: "Lower than peer average",
                  bgColor: "bg-orange-50",
                  color: "text-orange-600",
                  borderColor: "border-orange-200"
                } : {
                  label: "Aligned with peers",
                  bgColor: "bg-blue-50",
                  color: "text-blue-600",
                  borderColor: "border-blue-200"
                };
                return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${status_0.bgColor} ${status_0.color} border ${status_0.borderColor}`}>
											{status_0.label}
										</span>;
              })()}
							</div>
						</div>
					</div>
					<div className="flex-1 flex justify-center">
						<ConfidenceLevelIndicator currentScore={staticData_1.confidenceScore} />
					</div>
				</div>
			</div>;
  };

  // Competitor Comparison Section Component
  const CompetitorComparisonSection = ({
    ratingCategories: ratingCategories_1,
    staticData: staticData_2,
    productName: productName_0,
    productImage: productImage_0
  }) => {
    const comparisonObserver = useIntersectionObserver({
      threshold: 0.1
    });
    return <div ref={comparisonObserver.elementRef} className="px-8 py-16 border-b border-gray-200">
				<div className="flex items-center gap-3 mb-8">
					<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
						<IoIosGitCompare className="w-4 h-4 text-primary-primaryBlue" />
					</div>
					<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">COMPETITOR COMPARISON</div>
				</div>

				{/* Product Headers */}
				<div className="flex items-center gap-6 mb-4">
					<div className="flex-1 flex items-center justify-center gap-3">
						<div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
							<Image src={productImage_0 || "/Images/placeholder_medicine.svg"} alt={productName_0 || "Product"} width={48} height={48} className="w-full h-full object-contain" onError={e => {
              e.target.src = "/Images/placeholder_medicine.svg";
            }} />
						</div>
						<div className="text-xl font-bold text-gray-900 font-poppins text-center line-clamp-2">
							{productName_0 || "Product"}
						</div>
					</div>
					<div className="flex-1 flex items-center justify-center gap-3">
						<div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
							<Image src="/Images/placeholder_medicine.svg" alt={staticData_2.competitorName} width={48} height={48} className="w-full h-full object-contain" onError={e_0 => {
              e_0.target.src = "/Images/placeholder_medicine.svg";
            }} />
						</div>
						<div className="text-xl font-bold text-gray-900 font-poppins text-center line-clamp-2">
							{staticData_2.competitorName}
						</div>
					</div>
				</div>

				{/* Comparison Containers */}
				<div className="flex items-stretch gap-14 relative">
					{/* VS Line */}
					<div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 flex flex-col items-center z-10">
						<div className="w-px flex-1 bg-gray-300"></div>
						<div className="relative">
							<div className="w-14 h-14 rounded-full border border-gray-300 bg-white flex items-center justify-center shadow-sm">
								<span className="text-sm font-bold text-gray-600 font-poppins">VS</span>
							</div>
						</div>
						<div className="w-px flex-1 bg-gray-300"></div>
					</div>

					{/* Left Product Metrics */}
					<div className="flex-1">
						<div className="bg-white rounded-xl p-6 h-full">
							<div className="space-y-6">
								<div>
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-3">Performance Ratings</div>
									<div className="space-y-2.5">
										{ratingCategories_1.map(category_2 => <div key={category_2.tag} className="flex items-center justify-between">
												<span className="text-xs text-gray-700 font-poppins">{category_2.label}</span>
												<div className="flex items-center gap-1.5">
													<AnimatedProgressBar value={category_2.value} maxValue={5} shouldAnimate={comparisonObserver.hasBeenVisible} />
													<span className="text-xs font-bold text-primary-primaryBlue font-poppins min-w-[2rem] text-right">
														{category_2.value.toFixed(1)}
													</span>
												</div>
											</div>)}
									</div>
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-3">Clinical Concerns</div>
									{(() => {
                  const concerns = ratingCategories_1.filter(cat => cat.value > 0 && cat.value <= 3);
                  if (concerns.length === 0) {
                    return <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-2.5">
													<FiCheck className="w-4 h-4" />
													<span className="text-xs font-medium font-poppins">No major concerns</span>
												</div>;
                  }
                  return <div className="grid grid-cols-2 gap-2">
												{concerns.map(concern => <div key={concern.tag} className="flex items-center gap-2 bg-yellow-50 rounded-lg p-2.5">
														<div className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
															<span className="text-white text-[10px] font-bold">!</span>
														</div>
														<span className="text-xs font-medium text-gray-700 font-poppins">{concern.label}</span>
													</div>)}
											</div>;
                })()}
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-2">Accessibility Score</div>
									<div className="text-2xl font-bold font-poppins" style={{
                  color: staticData_2.accessibilityScore <= 50 ? '#6A5ACD' : staticData_2.accessibilityScore <= 75 ? '#1E5EFF' : '#059669'
                }}>
										<AnimatedCounter targetValue={staticData_2.accessibilityScore} shouldAnimate={comparisonObserver.hasBeenVisible} suffix="%" />
									</div>
									<div className="text-xs text-gray-600 font-poppins mt-1">{staticData_2.accessibilityCategory}</div>
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-2">Confidence Score</div>
									<div className="text-2xl font-bold font-poppins" style={{
                  color: staticData_2.confidenceScore <= 50 ? '#6A5ACD' : staticData_2.confidenceScore <= 75 ? '#1E5EFF' : '#059669'
                }}>
										<AnimatedCounter targetValue={staticData_2.confidenceScore} shouldAnimate={comparisonObserver.hasBeenVisible} suffix="%" />
									</div>
									<div className="text-xs text-gray-600 font-poppins mt-1">{staticData_2.confidenceCategory}</div>
								</div>
							</div>
						</div>
					</div>

					{/* Right Product Metrics */}
					<div className="flex-1">
						<div className="bg-white rounded-xl p-6 h-full">
							<div className="space-y-6">
								<div>
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-3">Performance Ratings</div>
									<div className="space-y-2.5">
										{ratingCategories_1.map(category_3 => {
                    const competitorValue = Math.max(1, category_3.value - (Math.random() * 1.5 - 0.5));
                    return <div key={category_3.tag} className="flex items-center justify-between">
													<span className="text-xs text-gray-700 font-poppins">{category_3.label}</span>
													<div className="flex items-center gap-1.5">
														<AnimatedProgressBar value={competitorValue} maxValue={5} shouldAnimate={comparisonObserver.hasBeenVisible} />
														<span className="text-xs font-bold text-primary-primaryBlue font-poppins min-w-[2rem] text-right">
															{competitorValue.toFixed(1)}
														</span>
													</div>
												</div>;
                  })}
									</div>
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-3">Clinical Concerns</div>
									<div className="grid grid-cols-2 gap-2">
										<div className="flex items-center gap-2 bg-yellow-50 rounded-lg p-2.5">
											<div className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
												<span className="text-white text-[10px] font-bold">!</span>
											</div>
											<span className="text-xs font-medium text-gray-700 font-poppins">Price/Affordability</span>
										</div>
										<div className="flex items-center gap-2 bg-yellow-50 rounded-lg p-2.5">
											<div className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
												<span className="text-white text-[10px] font-bold">!</span>
											</div>
											<span className="text-xs font-medium text-gray-700 font-poppins">Tolerability</span>
										</div>
									</div>
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-2">Accessibility Score</div>
									<div className="text-2xl font-bold font-poppins" style={{
                  color: '#1E5EFF'
                }}>
										<AnimatedCounter targetValue={68} shouldAnimate={comparisonObserver.hasBeenVisible} suffix="%" />
									</div>
									<div className="text-xs text-gray-600 font-poppins mt-1">Good</div>
								</div>

								<div className="pt-3 border-t border-gray-100">
									<div className="text-xs font-semibold text-gray-500 font-poppins uppercase tracking-wider mb-2">Confidence Score</div>
									<div className="text-2xl font-bold font-poppins" style={{
                  color: '#1E5EFF'
                }}>
										<AnimatedCounter targetValue={72} shouldAnimate={comparisonObserver.hasBeenVisible} suffix="%" />
									</div>
									<div className="text-xs text-gray-600 font-poppins mt-1">Confident</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>;
  };

  // Clinical Insight Section Component
  const ClinicalInsightSection = ({
    staticData: staticData_3
  }) => <div className="px-8 py-16 border-b border-gray-200">
			<div className="flex items-center gap-3 mb-6">
				<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
					<LuPencil className="w-4 h-4 text-primary-primaryBlue" />
				</div>
				<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">REAL-WORLD INSIGHT</div>
			</div>

			<div className="max-w-4xl">
				<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
					<div className="mb-6 pb-6 border-b border-gray-100">
						<h3 className="text-xl font-bold text-gray-900 font-poppins mb-2">What Would Improve Prescribing?</h3>
						<p className="text-sm text-gray-500 font-poppins">Prescriber Feedback & Clinical Insights</p>
					</div>

					<div className="relative">
						<div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-200 rounded-full"></div>
						<div className="pl-6">
							<p className="text-base leading-loose text-gray-700 font-poppins whitespace-pre-wrap">
								{staticData_3.clinicalInsight}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>;

  // Modal Header Component
  const ModalHeader = () => <div className="px-8 py-4 border-b-2 border-gray-300 flex-shrink-0 bg-white">
			<div className="flex justify-between items-center">
				<div className="flex-1">
					<div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">CLINICAL REVIEW REPORT</div>
					<div className="flex items-center gap-3">
						<div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
							<Image src={productImage || "/Images/placeholder_medicine.svg"} alt={productName || "Product"} width={48} height={48} className="w-full h-full object-contain" onError={e_1 => {
              e_1.target.src = "/Images/placeholder_medicine.svg";
            }} />
						</div>
						<div className="text-2xl font-bold text-gray-900 font-poppins">{productName || "Product"}</div>
					</div>
				</div>
				<div onClick={() => setIsModalOpen(false)} className="cursor-pointer hover:bg-gray-100 rounded-full p-2 transition-colors ml-4 flex-shrink-0">
					<CrossIcon />
				</div>
			</div>
		</div>;
  return <div className="rounded-lg overflow-hidden mt-2 sm:mt-3 bg-white cursor-pointer transition-shadow">
					<ReviewHeader review={review} isInModal={false} actions={<ReportReviewDialog reviewId={review?._id} />} />
				</div>
  // <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
  // 	<DialogTrigger asChild>
  // 	</DialogTrigger>
  // 	<DialogContent className="max-w-5xl max-h-[680px] p-0 bg-white rounded-none shadow-2xl overflow-hidden flex flex-col min-h-0 w-[1000px] border-2 border-gray-300 outline-none focus:outline-none [&>button]:hidden">
  // 		<DialogTitle className="sr-only">Clinical Review Report</DialogTitle>
  // 		<div className="flex flex-1 min-h-0 overflow-hidden bg-white">
  // 			<div className="w-full flex flex-col flex-1 min-h-0 overflow-hidden">
  // 				<ModalHeader />
  // 				<div className="flex-1 overflow-y-auto bg-white">
  // 					<FullReviewContent hidePerformanceRatings={false} />
  // 				</div>
  // 			</div>
  // 		</div>
  // 	</DialogContent>
  // </Dialog>
  ;
};
export default ReviewCard;